import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button as Button } from '@factorialco/f0-react'
import { Plus } from '@factorialco/f0-react/icons/app'
import { WorkflowRoleSelectorDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ApprovalSettingsModal } from './ApprovalSettingsModal'
import { ApproverSelection } from './ApproverSelection'
import { type StrategyItem } from './manualApproveHandlerUtils'

interface ApproverSelectionContainerProps {
  strategies: StrategyItem[]
  onAddStrategies: (strategies: StrategyItem[]) => void
  onDeleteStrategy: (strategyId: string) => void
  availableEmployees: Array<{
    id: number
    fullName: string
    avatar?: { url?: string | null } | null
  }>
  availableTeams: Array<{ id: number; name: string }>
  employeesLoading: boolean
  teamsLoading: boolean
}

export const ApproverSelectionContainer = ({
  strategies,
  onAddStrategies,
  onDeleteStrategy,
  availableEmployees,
  availableTeams,
  employeesLoading,
  teamsLoading,
}: ApproverSelectionContainerProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { company } = useCurrentSession()

  const { data: rolesData } = useQuery(WorkflowRoleSelectorDocument, {
    variables: { companyId: company.id },
  })
  const availableRoles = rolesData?.jobCatalog?.rolesConnection?.nodes || []
  const treeRoles = rolesData?.jobCatalog?.treeNodesConnection?.nodes || []

  const treeRoleNameMap = new Map<string, string>()
  treeRoles.forEach((role) => {
    if (role.uuid && role.name) {
      treeRoleNameMap.set(role.uuid, role.name)
    }
  })

  return (
    <Box gap="s8" width="full">
      <Text color="textPrimary" size="300" weight="medium">
        {i18n.t('workflows.sidepanel.approval.approvers.title')}
      </Text>

      {strategies.length > 0 && (
        <Box gap="s2" width="full">
          {strategies.map((strategy) => (
            <ApproverSelection
              key={strategy.id}
              strategy={strategy}
              onDelete={onDeleteStrategy}
              availableEmployees={availableEmployees}
              availableTeams={availableTeams}
              availableRoles={availableRoles}
              treeRoleNameMap={treeRoleNameMap}
            />
          ))}
        </Box>
      )}

      <Box flexDirection="row" justifyContent="flexStart" width="full" paddingTop="s8">
        <Button
          size="md"
          label={i18n.t('workflows.sidepanel.approval.add_approver')}
          icon={Plus}
          variant="outline"
          onClick={() => setIsModalOpen(true)}
        />
      </Box>

      <ApprovalSettingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddStrategies={onAddStrategies}
        availableEmployees={availableEmployees}
        availableTeams={availableTeams}
        employeesLoading={employeesLoading}
        teamsLoading={teamsLoading}
        existingStrategies={strategies}
      />
    </Box>
  )
}
